import React, { FC, useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import Alert from '@mui/material/Alert'
import { Container } from '../../types'
import { updateContainer } from '../../system/apitools'
import { containerFieldsChanged, ContainerLevelFields } from './videoFields'
import SectionWrapper from './LockedSectionWrapper'

interface ContainerSectionProps {
  container: Container
  onContainerChange: (updated: Container) => void
}

// Mirrors api/pkg/data/container.go's validateSlug and reservedSlugs — kept
// in sync by hand, same as isLikelyHlsUrl mirrors ffmpeg's own acceptance.
// Purely an early hint: the server is still the one authority on whether a
// slug is actually acceptable, and its error is what's shown on save.
const SLUG_PATTERN = /^[a-z0-9-]{3,64}$/
const RESERVED_SLUGS = new Set(['admin', 'api', 'embed', 'video', 'public', 'login', 'notfound'])
// Every container ID and video ID is a UUID, and a slug always wins ahead of
// an ID when a link is resolved — so a UUID-shaped slug would swallow every
// bare-ID link built on it. The server refuses these outright; this only says
// so sooner.
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/

const localSlugError = (value: string): string | null => {
  const normalised = value.trim().toLowerCase()
  if (normalised.length < 3 || normalised.length > 64) return 'Must be 3-64 characters'
  if (!SLUG_PATTERN.test(normalised)) return 'Only lowercase letters, numbers and hyphens'
  if (RESERVED_SLUGS.has(normalised)) return 'This slug is reserved'
  if (UUID_PATTERN.test(normalised)) return 'Cannot be an ID'
  return null
}

interface ContainerPatch {
  slug?: string
  ordering?: number
  isPrivate?: boolean
}

// ContainerSection edits the settings that belong to the CONTAINER, not any
// one version: the public slug, curated ordering and the private flag. None
// of this is ever written to chain, and publishing a new version never
// touches it — see the Container doc comment in types.ts.
const ContainerSection: FC<ContainerSectionProps> = ({ container, onContainerChange }) => {
  const queryClient = useQueryClient()

  const [slug, setSlug] = useState(container.slug)
  const [rawOrdering, setRawOrdering] = useState(String(container.ordering || 0))
  const [ordering, setOrdering] = useState(container.ordering || 0)
  const [isPrivate, setIsPrivate] = useState(!!container.private)
  const [suggestion, setSuggestion] = useState<string | null>(null)

  useEffect(() => {
    setSlug(container.slug)
    setRawOrdering(String(container.ordering || 0))
    setOrdering(container.ordering || 0)
    setIsPrivate(!!container.private)
    setSuggestion(null)
  }, [container.id, container.slug, container.ordering, container.private])

  // A nil pointer leaves that field untouched server-side (see
  // data.VideoStore.UpdateContainer), so the mutation only ever carries the
  // ONE field that actually changed — never the other two as a side effect.
  // Sending all three every time would mean a rejected slug (409, still
  // sitting in local state) gets silently resubmitted the next time the
  // admin merely changes ordering or private, failing a request that had
  // nothing to do with the slug.
  const updateMutation = useMutation({
    mutationFn: (patch: ContainerPatch) =>
      updateContainer(container.id, patch.slug, patch.ordering, patch.isPrivate),
    onSuccess: (updated) => {
      setSuggestion(null)
      onContainerChange(updated)
      queryClient.invalidateQueries({ queryKey: ['containers'] })
    },
    onError: (err: any) => {
      setSuggestion(err?.response?.data?.suggestion || null)
    },
  })

  const commit = (overrides?: Partial<ContainerLevelFields>) => {
    const fields: ContainerLevelFields = { slug, ordering, isPrivate, ...overrides }
    if (!containerFieldsChanged(fields, container)) return
    // The slug half of this is guarded by validity, not just by having
    // changed: an in-progress, not-yet-committed edit to an invalid slug
    // must never ride along on an unrelated ordering/private commit, and
    // must never fire a no-op request of its own either.
    const includeSlug = fields.slug !== container.slug && !localSlugError(fields.slug)
    const orderingChanged = fields.ordering !== (container.ordering || 0)
    const privateChanged = fields.isPrivate !== (container.private || false)
    if (!includeSlug && !orderingChanged && !privateChanged) return
    updateMutation.mutate({
      slug: includeSlug ? fields.slug : undefined,
      ordering: orderingChanged ? fields.ordering : undefined,
      isPrivate: privateChanged ? fields.isPrivate : undefined,
    })
  }

  const slugError = localSlugError(slug)
  const errorMessage = updateMutation.error
    ? (updateMutation.error as any)?.response?.data?.error || (updateMutation.error as Error).message
    : null

  const publicUrl = `${window.location.origin}/video/${slug}`

  return (
    <SectionWrapper title="Container" status="pending">
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        This container is this video's identity over time — its public URL and where it sits in
        listings. It is shared by every version, and publishing a new version never changes any
        of it.
      </Typography>

      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
          {suggestion && (
            <Button
              size="small"
              sx={{ ml: 1 }}
              onClick={() => {
                setSlug(suggestion)
                commit({ slug: suggestion })
              }}
            >
              Use &quot;{suggestion}&quot;
            </Button>
          )}
        </Alert>
      )}

      <TextField
        label="Slug"
        value={slug}
        onChange={(e) => {
          setSlug(e.target.value)
          setSuggestion(null)
        }}
        onBlur={() => {
          if (localSlugError(slug)) return
          commit()
        }}
        error={!!slugError}
        helperText={slugError || `Public URL: ${publicUrl}`}
        fullWidth
        margin="normal"
      />

      <TextField
        label="Ordering"
        type="text"
        value={rawOrdering}
        onChange={(e) => setRawOrdering(e.target.value)}
        onBlur={() => {
          const parsed = parseInt(rawOrdering, 10)
          const clamped = Number.isNaN(parsed) ? 0 : Math.max(0, parsed)
          setRawOrdering(String(clamped))
          setOrdering(clamped)
          commit({ ordering: clamped })
        }}
        helperText="Position in listings, ascending. 0 = unordered (sorts after ordered containers, newest first)"
        margin="normal"
        sx={{ width: 320, display: 'block' }}
      />

      <FormControlLabel
        sx={{ display: 'block', mt: 1 }}
        control={
          <Checkbox
            checked={isPrivate}
            onChange={(e) => {
              setIsPrivate(e.target.checked)
              commit({ isPrivate: e.target.checked })
            }}
          />
        }
        label="Private — hide from the public listing (still reachable by direct link or embed)"
      />
    </SectionWrapper>
  )
}

export default ContainerSection
